import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { useViewList } from "@/context/ViewListContext";
import { AlternatingMessages, Button, Loader, PageMainContent, useGoBack } from "@vixi/ui";
import { getI18n, LangKey, useTranslation } from "@vixi/localization";
import { useCreateAvatar } from "@/hooks/api/avatars/useCreateAvatar";
import { useNavigate } from "@vixi/router";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { CustomPreferenceStep } from "@/partials/create/avatar/CustomPreferenceStep";
import { ConfirmNewCharacterStep } from "@/partials/create/avatar/ConfirmNewCharacterStep";
import { paymentInfoQueryOptions } from "@vixi/api";
import { allAvatarsCountQueryOptions } from "@/queries/avatars/allAvatarsCountQuery";
import {
  drawingStyleOptions,
  PreferenceDict,
  PreferenceTree,
} from "@/partials/create/course/CreateCharacterStep/CharacterPreferences/PreferenceForm";
import {
  CharacterPreferenceOption,
  SelectCharacterPreferences,
} from "@/partials/create/course/CreateCharacterStep/CharacterPreferences/SelectCharacterPreferences";
import { FileUploadBox } from "@vixi/ui";

// Assets
import HeadshotBubbleEn from "@assets/bubbles/headshot.png";
import HeadshotBubbleAr from "@assets/bubbles/ar-headshot.png";
import { RouteOptionsById, RouterIds } from "@vixi/router";

// Constants
const routeId = "/_app/avatars/create" satisfies RouterIds;

export const CreateAvatarPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient } }) => {
    // Pre-fetch payment info and avatars count for upgrade popup check
    // Use Promise.allSettled to prevent blocking if queries fail
    await Promise.allSettled([
      queryClient.ensureQueryData(paymentInfoQueryOptions()).catch(() => {}),
      queryClient.ensureQueryData(allAvatarsCountQueryOptions()).catch(() => {}),
    ]);
  },
};

const HeadshotBubble = { ar: HeadshotBubbleAr, en: HeadshotBubbleEn } as const;
const create_avatar_preference_tree = {
  title: "Avatars.GenderTitle",
  key: "gender" as const,
  options: [
    {
      label: "Gender.Male",
      value: "male",
      images: [
        "/images/avatars/preferences/male/male-traditional.png",
        "/images/avatars/preferences/male/male-morgan.png",
      ],
    },
    {
      label: "Gender.Female",
      value: "female",
      images: [
        "/images/avatars/preferences/female/female-hijabi.webp",
        "/images/avatars/preferences/female/female.webp",
      ],
      subPreferences: [
        {
          title: "Avatars.HijabiTitle",
          key: "hijabi" as const,
          options: [
            {
              label: "Avatars.Hijabi",
              images: ["/images/avatars/preferences/female/hijabi/index.png"],
              value: "hijabi",
            },
            {
              label: "Avatars.NonHijabi",
              images: ["/images/avatars/preferences/female/nonhijabi/index.png"],
              value: "nonhijabi",
            },
          ],
        },
      ],
    },
  ],
} satisfies PreferenceTree<string, string>;

const maleOutfitOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Outfit.BlackTshirt",
    value: getI18n().t("Avatars.Outfit.BlackTshirt", "en"),
    image: "/images/avatars/preferences/male/outfits/black-tshirt.webp",
  },
  {
    label: "Avatars.Outfit.GraySuit",
    value: getI18n().t("Avatars.Outfit.GraySuit", "en"),
    image: "/images/avatars/preferences/male/outfits/gray-suit.png",
  },
  {
    label: "Avatars.Outfit.RedShirt",
    value: getI18n().t("Avatars.Outfit.RedShirt", "en"),
    image: "/images/avatars/preferences/male/outfits/red-shirt.png",
  },
  {
    label: "Avatars.Outfit.BrownTurtleneck",
    value: getI18n().t("Avatars.Outfit.BrownTurtleneck", "en"),
    image: "/images/avatars/preferences/male/outfits/brown-turtleneck.webp",
  },
];

const malePoseOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Pose.RockingChair",
    value: getI18n().t("Avatars.Pose.RockingChair", "en"),
    image: "/images/avatars/preferences/male/poses/rocking-chair.png",
  },
  {
    label: "Avatars.Pose.StandingUpfront",
    value: getI18n().t("Avatars.Pose.StandingUpfront", "en"),
    image: "/images/avatars/preferences/male/poses/standing-upfront.png",
  },
  {
    label: "Avatars.Pose.HoldingDumbbells",
    value: getI18n().t("Avatars.Pose.HoldingDumbbells", "en"),
    image: "/images/avatars/preferences/male/poses/holding-dumbbells.png",
  },
  {
    label: "Avatars.Pose.SittingWithLaptop",
    value: getI18n().t("Avatars.Pose.SittingWithLaptop", "en"),
    image: "/images/avatars/preferences/male/poses/sitting-with-laptop.png",
  },
];

const nonHijabiOutfitOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Outfit.WhiteDress",
    value: getI18n().t("Avatars.Outfit.WhiteDress", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/outfits/white-dress.png",
  },
  {
    label: "Avatars.Outfit.LongBrownJacket",
    value: getI18n().t("Avatars.Outfit.LongBrownJacket", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/outfits/long-brown-jacket.png",
  },
  {
    label: "Avatars.Outfit.LeatherSkirt",
    value: getI18n().t("Avatars.Outfit.LeatherSkirt", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/outfits/leather-skirt.png",
  },
  {
    label: "Avatars.Outfit.BlackDress",
    value: getI18n().t("Avatars.Outfit.BlackDress", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/outfits/black-dress.png",
  },
];

const nonHijabiPoseOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Pose.RockingChair",
    value: getI18n().t("Avatars.Pose.RockingChair", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/poses/rocking-chair.png",
  },
  {
    label: "Avatars.Pose.StandingUpfront",
    value: getI18n().t("Avatars.Pose.StandingUpfront", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/poses/standing-upfront.png",
  },
  {
    label: "Avatars.Pose.HoldingSkincare",
    value: getI18n().t("Avatars.Pose.HoldingSkincare", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/poses/holding-skincare.png",
  },
  {
    label: "Avatars.Pose.SittingWithLaptop",
    value: getI18n().t("Avatars.Pose.SittingWithLaptop", "en"),
    image: "/images/avatars/preferences/female/nonhijabi/poses/sitting-with-laptop.png",
  },
];

const hijabiOutfitOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Outfit.Hijabi.WhiteDress",
    value: getI18n().t("Avatars.Outfit.Hijabi.WhiteDress", "en"),
    image: "/images/avatars/preferences/female/hijabi/outfits/white-dress.png",
  },
  {
    label: "Avatars.Outfit.Hijabi.LongBrownJacket",
    value: getI18n().t("Avatars.Outfit.Hijabi.LongBrownJacket", "en"),
    image: "/images/avatars/preferences/female/hijabi/outfits/long-brown-jacket.png",
  },
  {
    label: "Avatars.Outfit.Hijabi.LeatherSkirt",
    value: getI18n().t("Avatars.Outfit.Hijabi.LeatherSkirt", "en"),
    image: "/images/avatars/preferences/female/hijabi/outfits/black-skirt.png",
  },
  {
    label: "Avatars.Outfit.Hijabi.BlackAbaya",
    value: getI18n().t("Avatars.Outfit.Hijabi.BlackAbaya", "en"),
    image: "/images/avatars/preferences/female/hijabi/outfits/black-abaya.png",
  },
];

const hijabiPoseOptions: CustomPreferenceOption[] = [
  {
    label: "Avatars.Pose.RockingChair",
    value: getI18n().t("Avatars.Pose.RockingChair", "en"),
    image: "/images/avatars/preferences/female/hijabi/poses/rocking-chair.png",
  },
  {
    label: "Avatars.Pose.StandingUpfront",
    value: getI18n().t("Avatars.Pose.StandingUpfront", "en"),
    image: "/images/avatars/preferences/female/hijabi/poses/standing-upfront.png",
  },
  {
    label: "Avatars.Pose.HoldingSkincare",
    value: getI18n().t("Avatars.Pose.HoldingSkincare", "en"),
    image: "/images/avatars/preferences/female/hijabi/poses/holding-skincare.png",
  },
  {
    label: "Avatars.Pose.SittingWithLaptop",
    value: getI18n().t("Avatars.Pose.SittingWithLaptop", "en"),
    image: "/images/avatars/preferences/female/hijabi/poses/sitting-with-laptop.png",
  },
];

const styleOptions = {
  male: drawingStyleOptions("/images/avatars/preferences/male/styles"),
  nonhijabi: drawingStyleOptions("/images/avatars/preferences/female/nonhijabi/styles"),
  hijabi: drawingStyleOptions("/images/avatars/preferences/female/hijabi/styles"),
};

// Types
export interface CustomPreferenceOption {
  label: LangKey;
  value: string;
  image: string;
}
type BasePreferences = PreferenceDict<typeof create_avatar_preference_tree>;

export interface CreateAvatarFormValues extends BasePreferences {
  image: File[];
  outfit: string;
  pose: string;
  style: string;
}

/**
 * goBack is dynamic: changes based on the viewList state.
 * If the user is on the first step, "goBack" will navigate to /app/avatars, otherwise it will go back to the previous step.
 */
function RouteComponent() {
  const { t, byLocale, tFile } = useTranslation();
  const navigate = useNavigate();

  const createAvatarForm = useForm<CreateAvatarFormValues>({
    defaultValues: {
      image: [],
      hijabi: "",
    },
  });
  const { watch, setValue, control, getValues } = createAvatarForm;

  const [outfitSelection, poseSelection, styleSelection] = useMemo(() => {
    const { gender, hijabi } = getValues();

    let outfitSelection: CustomPreferenceOption[];
    let poseSelection: CustomPreferenceOption[];
    let styleSelection: ReturnType<typeof drawingStyleOptions>;
    if (gender === "female") {
      outfitSelection = hijabi === "hijabi" ? hijabiOutfitOptions : nonHijabiOutfitOptions;
      poseSelection = hijabi === "hijabi" ? hijabiPoseOptions : nonHijabiPoseOptions;
      styleSelection = hijabi === "hijabi" ? styleOptions.hijabi : styleOptions.nonhijabi;
    } else {
      outfitSelection = maleOutfitOptions;
      poseSelection = malePoseOptions;
      styleSelection = styleOptions.male;
    }

    return [outfitSelection, poseSelection, styleSelection];
  }, [watch("gender"), watch("hijabi")]);

  const onPreferenceSelected = useCallback((field: keyof CreateAvatarFormValues, value: string) => {
    setValue(field, value);
    viewList.nextStep();
  }, []);

  const viewList = useViewList();

  const goBack = useMemo(() => {
    return () => {
      if (viewList.activeStep === 0) {
        history.back();
      } else {
        viewList.prevStep();
      }
    };
  }, [viewList.activeStep, viewList.prevStep]);

  useGoBack(goBack, {
    allowChanges: true,
  });

  // Avatar generation
  const {
    mutateAsync: createAvatar,
    data: generatedAvatar,
    isSuccess: avatarGenerated,
    isIdle,
    isError,
    uploadProgress,
  } = useCreateAvatar();

  const handleCreateAvatar = useCallback(() => {
    const { outfit, image, ...vals } = getValues();

    createAvatar({
      ...vals,
      clothes: outfit,
      image: image[0],
    });

    viewList.nextStep();
  }, [createAvatar]);

  return (
    <PageMainContent scrollable className="pb-2.5" psX="medium">
      {isIdle ? (
        <FormProvider {...createAvatarForm}>
          <ViewList {...viewList}>
            {/* Upload Image */}
            <ViewListItem>
              <div className="m-auto">
                <Controller
                  control={control}
                  name="image"
                  render={({ field }) => (
                    <FileUploadBox
                      accept="image/jpeg, image/png, image/webp"
                      image={{
                        src: byLocale({
                          ...HeadshotBubble,
                          fallback: tFile("/images/bubbles/", "headshot.png"),
                        }),
                        alt: "Fox Icon",
                      }}
                      imageSize={{ width: "509px", height: "509px" }}
                      dropzoneLabel={t("Action.DropPicture")}
                      imageMarginBlock={byLocale({ ar: "-160px 0", fallback: "-170px 0" })}
                      {...field}
                    />
                  )}
                />
              </div>

              {watch("image")?.length > 0 && (
                <div className="absolute end-20 bottom-12.5">
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    onClick={viewList.nextStep}
                  >
                    {t("Action.Next")}
                  </Button>
                </div>
              )}
            </ViewListItem>

            {/* Defining Preferences */}
            <ViewListItem>
              <SelectCharacterPreferences
                preferencesForm={createAvatarForm}
                preferenceTree={create_avatar_preference_tree}
                onCompleted={viewList.nextStep}
              />
            </ViewListItem>

            {/* Preferred Outfit */}
            <ViewListItem>
              <CustomPreferenceStep
                field="outfit"
                text={{
                  header: "CreateAvatar.DescribePreferredOutfit",
                  placeholder: "CreateAvatar.DescribePreferredOutfitPlaceholder",
                  cta: "CreateAvatar.ChooseOutfit",
                  nextBtnText: "Action.Next",
                  useAvatar: "CreateAvatar.UseThisOutfit",
                }}
                onNext={viewList.nextStep}
                options={outfitSelection}
              />
            </ViewListItem>

            {/* Preferred Pose */}
            <ViewListItem>
              <CustomPreferenceStep
                field="pose"
                text={{
                  header: "CreateAvatar.DescribePreferredPose",
                  placeholder: "CreateAvatar.DescribePreferredPosePlaceholder",
                  cta: "CreateAvatar.ChoosePose",
                  nextBtnText: "Action.Next",
                  useAvatar: "CreateAvatar.UseThisPose",
                }}
                onNext={viewList.nextStep}
                options={poseSelection}
              />
            </ViewListItem>

            {/* Preferred Style */}
            <ViewListItem>
              <div className="my-auto flex flex-col items-center">
                <h4 className="text-center text-[24px] font-semibold">{t("CreateAvatar.YourPreferredStyle")}:</h4>
                <div className="mt-12.5 flex gap-7.25">
                  {styleSelection.map((option, i) => (
                    <CharacterPreferenceOption
                      key={i}
                      option={option}
                      onClick={(option) => {
                        onPreferenceSelected("style", option.value);
                        handleCreateAvatar();
                      }}
                    />
                  ))}
                </div>
              </div>
            </ViewListItem>
          </ViewList>
        </FormProvider>
      ) : generatedAvatar ? (
        <ConfirmNewCharacterStep generatedAvatar={generatedAvatar[0]} />
      ) : (
        <div className="m-auto">
          <Loader
            isFailed={isError}
            onRetry={handleCreateAvatar}
            isCompleted={avatarGenerated}
            width={300}
          />
          <p className="mt-2 text-center">
            <AlternatingMessages
              complete={avatarGenerated || isError}
              messages={["LoadingMessage.Stretch", "LoadingMessage.GeneratingYourAvatar"]}
            />
          </p>
        </div>
      )}
    </PageMainContent>
  );
}
